import React, { createContext, useEffect, useState } from "react"
import { Layout, Spin, Tooltip } from "antd"
import { Footer } from "antd/lib/layout/layout"
import css from "./MainLayout.module.css"
import packageJson from "../../../package.json"
import { CourseSearchDisplayMode, GetCompanyQuery, SettingEndUserAppQuery, useGetCompanyQuery, useSettingEndUserAppQuery } from "../../generated/graphql"
import "../../app.css"
import { useMediaSize } from "./GetWindowDimensions/GetWindowDimensions"
import { getMobileOperatingSystem } from "../../styles/layout"
import { useBuildInfo } from "../../helpers/buildInfo"
import AuthHeader from "../../containers/auth/AuthHeader"
import { blue } from "@ant-design/colors"
import { useTranslation } from "react-i18next"
import { URL_AGB, URL_DATENSCHUTZ, URL_WIDERRUF } from "../../models/url"
import { layoutContentId } from "../../helpers/embedMode"

const { Content } = Layout

// We define our type for the context properties right here
type MainContextProps = {
    userAuthDate: (date: Date) => any
}

// we initialise them without default values, to make that happen, we
// apply the Partial helper type.
export const MainContext = createContext<Partial<MainContextProps>>({})

// Settings Context -------------------------------------------------------------------------------------
export type AppSettings = SettingEndUserAppQuery["settingEndUserAppPublic"]

const defaultSettingEndUserApp: AppSettings = {
    // id: "1",
    colors: {},
    courseTable: {},
    courseDetails: {},
    filters: {},
    textEntries: {},
    optionalInputFields: undefined,
    hideAfterDays: 1,
    passwordlessLoginEnabled: false,
    passwordlessIdCodeLength: 6,
    // GraphQL-Codegen liefert aktuell Enum I18n; bis Schema stabil ist, setzen wir einen kompatiblen Default
    i18n: "DeDU" as unknown as AppSettings["i18n"],
    courseSearchDisplayMode: CourseSearchDisplayMode.Table,
    courseSearchPageSize: 10,
    courseSearchGridMaxColumns: 3,
    courseSearchGridGap: 15,
    withdrawalButtonEnabled: false,
    creditRedemptionEnabled: false,
    discountCodesEnabled: false,
    showTotalPriceInCheckout: true,
    billingAddressInCheckoutEnabled: false,
    showInterestListsByDefault: true,
    interestListExplanation: null,
    calendarFeedEnabled: false,
    tarif595Enabled: false,
}

export const SettingsContext = createContext<AppSettings>(defaultSettingEndUserApp)

// ----------------------------------------------------------------------------------------------------

const Main = ({ ...props }) => {
    const { t } = useTranslation()
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const oS = getMobileOperatingSystem()
    // Build-Metadaten des ausgelieferten Bundles (Commit/Datum) für den Footer
    const buildInfo = useBuildInfo()
    const [loading] = useState(false)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [authDate, setAuthDate] = useState(new Date())
    const userAuthDate = (date: React.SetStateAction<Date>) => {
        setAuthDate(date)
    }

    // SETTINGS FOR THE END_USER_APP -------------------------------------------------------------------------------------
    const { data: appSettingsData, loading: loadingAppSettingsData } = useSettingEndUserAppQuery()
    const fetchedAppSettings: AppSettings | undefined = appSettingsData?.settingEndUserAppPublic
    const [appSettings, setAppSettings] = useState<AppSettings>(defaultSettingEndUserApp)

    // console.log("fetchedAppSettings", fetchedAppSettings)

    useEffect(() => {
        if (fetchedAppSettings && !loadingAppSettingsData) {
            // Vom Server gelieferte Settings über die Defaults legen.
            const newAppSettings: AppSettings = { ...appSettings, ...fetchedAppSettings }
            setAppSettings(newAppSettings)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchedAppSettings])

    // -------------------------------------------------------------------------------------------------------------------

    const { data: companyData } = useGetCompanyQuery()
    const companyName: GetCompanyQuery["companyPublic"]["name"] = companyData?.companyPublic.name ?? ""
    const companySettings = companyData?.companyPublic

    const tenantId = process.env.NODE_ENV === "development" ? `${process.env.REACT_APP_APPLICATION_TYPE}` : ""
    const currentSize = process.env.NODE_ENV === "development" ? media : null

    // Prüfe ob App in iFrame läuft (für interne Routen)
    const isInIframe = window.parent !== window || window.location.search.includes("parentUrl")

    const footerInfos = (device: "mobile" | "desktop") => {
        const versionText = "KursOrganizer V"
        // shortenedVersion is only the numbers before the second dot
        const shortenedVersion = packageJson.version.split(".")[0] + "." + packageJson.version.split(".")[1]
        const mode = process.env.REACT_APP_ENV

        // AGB und Datenschutz Links - werden IMMER angezeigt
        const externalAGB = companySettings?.agb
        const externalPrivacy = companySettings?.privacyStatement

        // Kurzer Git-Hash des aktuell ausgelieferten Builds (Rückverfolgbarkeit).
        // Wird NUR im Klick-Tooltip angezeigt, NICHT im sichtbaren Footer-Text.
        // Fehlt im Dev-Modus (kein meta.json) -> dann ohne Hash.
        const shortHash = buildInfo?.commit && buildInfo.commit !== "unknown" ? buildInfo.commit : ""

        // Version-Info Text zusammenbauen (sichtbar: nur major.minor)
        let versionInfoText = `${versionText} ${shortenedVersion}`
        if (mode !== "production") {
            versionInfoText += ` | ${process.env.REACT_APP_ENV} | ${companyName} | ${tenantId} | size: ${currentSize} | oS: ${oS}`
        } else {
            versionInfoText += device === "desktop" ? ` | ${companyName}` : ""
        }

        // Berechne vollständige URLs für interne Routen
        // const agbUrl = externalAGB || (isInIframe ? `${window.location.origin}${window.location.pathname}#${URL_AGB}` : "#")
        // const privacyUrl = externalPrivacy || (isInIframe ? `${window.location.origin}${window.location.pathname}#${URL_DATENSCHUTZ}` : "#")
        const agbUrl = `${window.location.origin}${window.location.pathname}#${URL_AGB}`
        const privacyUrl = `${window.location.origin}${window.location.pathname}#${URL_DATENSCHUTZ}`

        // Ausführliche Build-ID im Tooltip (volle Version + Commit + Build-Datum)
        const buildVersionTooltip = (
            <div style={{ textAlign: "left", whiteSpace: "nowrap" }}>
                <div>v{packageJson.version}</div>
                {shortHash && (
                    <div>
                        commit {shortHash}
                        {buildInfo && buildInfo.commitCount > 0 ? ` · #${buildInfo.commitCount}` : ""}
                    </div>
                )}
                {buildInfo?.buildDateIso && <div>{buildInfo.buildDateIso}</div>}
            </div>
        )

        // Widerruf-Link nur rendern, wenn der Tenant den Schalter aktiviert hat
        const withdrawalEnabled = appSettings.withdrawalButtonEnabled === true
        const withdrawalUrl = `${window.location.origin}${window.location.pathname}#${URL_WIDERRUF}`

        // Neutraler, immer sichtbarer innerer Container (verhindert das Mobile-Display:none
        // der äußeren .versionInfo-Hülle, die den Inhalt auf Mobile ausblendete).
        return (
            <>
                <div className={css.footerInner} style={{ cursor: "pointer" }}>
                    <a href={agbUrl} target='_blank' rel='noopener noreferrer' className={css.footerLink}>
                        AGB
                    </a>
                    <span className={css.footerSeparator}>|</span>
                    <a href={privacyUrl} target='_blank' rel='noopener noreferrer' className={css.footerLink}>
                        Datenschutz
                    </a>
                    {withdrawalEnabled && (
                        <>
                            <span className={css.footerSeparator}>|</span>
                            <a href={withdrawalUrl} className={css.footerLinkHighlight}>
                                {t("footer.withdrawal", "Vertrag widerrufen")}
                            </a>
                        </>
                    )}
                    <span className={css.footerSeparator}>|</span>
                    <Tooltip title={buildVersionTooltip} trigger={["click"]}>
                        {versionInfoText}
                    </Tooltip>
                </div>
            </>
        )
    }

    return (
        <MainContext.Provider
            value={{
                userAuthDate: (date: React.SetStateAction<Date>) => userAuthDate(date),
            }}
        >
            <Layout className={css.root}>
                <Content className={css.content} id={layoutContentId()}>
                    <AuthHeader
                        /* setAuthDate={(authDate: Date) => setAuthDate(authDate)} */
                        currentLocation={props.currentLocation}
                        htmlId='authHeader'
                    />
                    <SettingsContext.Provider value={appSettings}>
                        <Spin spinning={loading}>
                            <div>{props.children}</div>
                        </Spin>
                    </SettingsContext.Provider>
                    <Footer className={css.footer} style={{ padding: 0 }} id='footer'>
                        <div className={css.versionInfo}>{footerInfos("desktop")}</div>
                        <div className={css.versionInfoMobile}>{footerInfos("mobile")}</div>
                    </Footer>
                </Content>
            </Layout>
        </MainContext.Provider>
    )
}

export default Main
